"use client"

import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { useLanguage } from "../context/LanguageContext"
import "./ProjectKcCalculator.css"

const ProjectKcCalculator = () => {
  const { id } = useParams()
  let projectId = id
  const { t, direction } = useLanguage()
  const [project, setProject] = useState(null)
  const [cropTypes, setCropTypes] = useState([])
  const [selectedCropType, setSelectedCropType] = useState("")
  const [cropData, setCropData] = useState({
    name: "",
    plantingDate: new Date().toISOString().split("T")[0],
    harvestDate: "",
    initialStage: 20,
    developmentStage: 30,
    midSeasonStage: 40,
    lateSeasonStage: 30,
    kcInitial: 0.3,
    kcMid: 1.15,
    kcEnd: 0.7,
  })
  const [kcValues, setKcValues] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch project details
        const projectRes = await axios.get(`${API_URL}/api/crops/calculate-kc-curve/${projectId}`)
        setProject(projectRes.data)

        // Fetch crop types
        const cropTypesRes = await axios.get(`${API_URL}/api/crop-types`)
        setCropTypes(cropTypesRes.data)

        // Fetch project crop data if exists
        try {
          const cropDataRes = await axios.get(`${API_URL}/api/crops/${projectId}`)
          if (cropDataRes.data) {
            setCropData(cropDataRes.data)
            setSelectedCropType(cropDataRes.data.cropType || "")

            // If we have crop data, calculate KC values
            calculateKcValues(cropDataRes.data)
          }
        } catch (err) {
          console.log("No existing crop data found for this project")
        }

        setLoading(false)
      } catch (err) {
        console.error(err)
        setError("Failed to load project data")
        setLoading(false)
      }
    }

    if (projectId) {
      fetchProjectData()
    }
  }, [projectId])

  const handleCropTypeChange = async (e) => {
    const cropTypeId = e.target.value
    setSelectedCropType(cropTypeId)

    if (cropTypeId) {
      try {
        // Fetch crop type details
        const cropTypeRes = await axios.get(`${API_URL}/api/crop-types/${cropTypeId}`)
        const cropTypeData = cropTypeRes.data

        // Update crop data with crop type values
        const updatedCropData = {
          ...cropData,
          cropType: cropTypeId,
          name: cropTypeData.name,
          initialStage: cropTypeData.initialStage || cropData.initialStage,
          developmentStage: cropTypeData.developmentStage || cropData.developmentStage,
          midSeasonStage: cropTypeData.midSeasonStage || cropData.midSeasonStage,
          lateSeasonStage: cropTypeData.lateSeasonStage || cropData.lateSeasonStage,
          kcInitial: cropTypeData.kcInitial || cropData.kcInitial,
          kcMid: cropTypeData.kcMid || cropData.kcMid,
          kcEnd: cropTypeData.kcEnd || cropData.kcEnd,
        }

        setCropData(updatedCropData)
        calculateKcValues(updatedCropData)
      } catch (err) {
        console.error(err)
        setError("Failed to load crop type data")
      }
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target

    // Update crop data
    const updatedCropData = {
      ...cropData,
      [name]: value,
    }

    setCropData(updatedCropData)

    // Recalculate KC values if we have all necessary data
    if (
      updatedCropData.plantingDate &&
      updatedCropData.initialStage &&
      updatedCropData.developmentStage &&
      updatedCropData.midSeasonStage &&
      updatedCropData.lateSeasonStage &&
      updatedCropData.kcInitial &&
      updatedCropData.kcMid &&
      updatedCropData.kcEnd
    ) {
      calculateKcValues(updatedCropData)
    }
  }

  const calculateHarvestDate = (plantingDate, totalDays) => {
    const date = new Date(plantingDate)
    date.setDate(date.getDate() + totalDays)
    return date.toISOString().split("T")[0]
  }

  const calculateKcValues = (data) => {
    // Calculate total growing period
    const totalDays =
      Number.parseInt(data.initialStage) +
      Number.parseInt(data.developmentStage) +
      Number.parseInt(data.midSeasonStage) +
      Number.parseInt(data.lateSeasonStage)

    // Update harvest date based on planting date and total days
    const harvestDate = calculateHarvestDate(data.plantingDate, totalDays)
    setCropData((prev) => ({ ...prev, harvestDate }))

    // Calculate KC values for each day of the growing period
    const kcValues = []
    let currentDay = 0

    // Initial stage - constant KC value
    for (let i = 0; i < Number.parseInt(data.initialStage); i++) {
      currentDay++
      kcValues.push({
        day: currentDay,
        kc: Number.parseFloat(data.kcInitial),
        stage: "initial",
      })
    }

    // Development stage - linear increase from KC initial to KC mid
    for (let i = 0; i < Number.parseInt(data.developmentStage); i++) {
      currentDay++
      const progress = i / Number.parseInt(data.developmentStage)
      const kc =
        Number.parseFloat(data.kcInitial) +
        progress * (Number.parseFloat(data.kcMid) - Number.parseFloat(data.kcInitial))
      kcValues.push({
        day: currentDay,
        kc: Number.parseFloat(kc.toFixed(2)),
        stage: "development",
      })
    }

    // Mid-season stage - constant KC value
    for (let i = 0; i < Number.parseInt(data.midSeasonStage); i++) {
      currentDay++
      kcValues.push({
        day: currentDay,
        kc: Number.parseFloat(data.kcMid),
        stage: "mid-season",
      })
    }

    // Late season stage - linear decrease from KC mid to KC end
    for (let i = 0; i < Number.parseInt(data.lateSeasonStage); i++) {
      currentDay++
      const progress = i / Number.parseInt(data.lateSeasonStage)
      const kc =
        Number.parseFloat(data.kcMid) - progress * (Number.parseFloat(data.kcMid) - Number.parseFloat(data.kcEnd))
      kcValues.push({
        day: currentDay,
        kc: Number.parseFloat(kc.toFixed(2)),
        stage: "late-season",
      })
    }

    setKcValues(kcValues)
  }

  const saveCropData = async () => {
    try {
      setSaving(true)
      setError(null)

      // Save crop data to project
      await axios.post(`${API_URL}/api/projects/${projectId}/crop-data`, cropData)

      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      console.error(err)
      setError("Failed to save crop data")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="kc-calculator-loading">
        <div className="spinner"></div>
        <p>{t("Loading project data...")}</p>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="kc-calculator-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{t("Project not found")}</p>
        <Link to="/dashboard" className="back-button">
          {t("Back to Dashboard")}
        </Link>
      </div>
    )
  }

  return (
    <div className="kc-calculator-container" dir={direction}>
      <div className="kc-calculator-header">
        <h1>{t("Crop Coefficient (Kc) Calculator")}</h1>
        <p className="subtitle">
          {t("Project")}: {project.name} - {project.farm?.name}
        </p>
      </div>

      {error && (
        <div className="alert-error kc-calculator-alert">
          <i className="fas fa-exclamation-circle"></i>
          {t(error)}
        </div>
      )}

      {saved && (
        <div className="alert-success kc-calculator-alert">
          <i className="fas fa-check-circle"></i>
          {t("Crop data saved successfully")}
        </div>
      )}

      <div className="kc-calculator-grid">
        <div className="kc-calculator-inputs">
          <div className="card">
            <div className="card-header">
              <h2>{t("Crop Data")}</h2>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label htmlFor="cropType">{t("Crop Type")}</label>
                <select id="cropType" value={selectedCropType} onChange={handleCropTypeChange} className="form-control">
                  <option value="">{t("Select a crop type")}</option>
                  {cropTypes.map((cropType) => (
                    <option key={cropType._id} value={cropType._id}>
                      {cropType.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="name">{t("Crop Name")}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={cropData.name}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder={t("Enter crop name")}
                />
              </div>

              <div className="form-group">
                <label htmlFor="plantingDate">{t("Planting Date")}</label>
                <input
                  type="date"
                  id="plantingDate"
                  name="plantingDate"
                  value={cropData.plantingDate}
                  onChange={handleInputChange}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="harvestDate">{t("Harvest Date (Calculated)")}</label>
                <input
                  type="date"
                  id="harvestDate"
                  name="harvestDate"
                  value={cropData.harvestDate}
                  readOnly
                  className="form-control readonly"
                />
              </div>

              <div className="form-section">
                <h3>{t("Growth Stages (days)")}</h3>
                <div className="form-row">
                  <div className="form-group half">
                    <label htmlFor="initialStage">{t("Initial")}</label>
                    <input
                      type="number"
                      id="initialStage"
                      name="initialStage"
                      value={cropData.initialStage}
                      onChange={handleInputChange}
                      className="form-control"
                      min="1"
                    />
                  </div>
                  <div className="form-group half">
                    <label htmlFor="developmentStage">{t("Development")}</label>
                    <input
                      type="number"
                      id="developmentStage"
                      name="developmentStage"
                      value={cropData.developmentStage}
                      onChange={handleInputChange}
                      className="form-control"
                      min="1"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group half">
                    <label htmlFor="midSeasonStage">{t("Mid-Season")}</label>
                    <input
                      type="number"
                      id="midSeasonStage"
                      name="midSeasonStage"
                      value={cropData.midSeasonStage}
                      onChange={handleInputChange}
                      className="form-control"
                      min="1"
                    />
                  </div>
                  <div className="form-group half">
                    <label htmlFor="lateSeasonStage">{t("Late Season")}</label>
                    <input
                      type="number"
                      id="lateSeasonStage"
                      name="lateSeasonStage"
                      value={cropData.lateSeasonStage}
                      onChange={handleInputChange}
                      className="form-control"
                      min="1"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>{t("Crop Coefficients (Kc)")}</h3>
                <div className="form-row">
                  <div className="form-group third">
                    <label htmlFor="kcInitial">{t("Initial (Kc ini)")}</label>
                    <input
                      type="number"
                      id="kcInitial"
                      name="kcInitial"
                      value={cropData.kcInitial}
                      onChange={handleInputChange}
                      className="form-control"
                      step="0.01"
                      min="0"
                      max="2"
                    />
                  </div>
                  <div className="form-group third">
                    <label htmlFor="kcMid">{t("Mid-Season (Kc mid)")}</label>
                    <input
                      type="number"
                      id="kcMid"
                      name="kcMid"
                      value={cropData.kcMid}
                      onChange={handleInputChange}
                      className="form-control"
                      step="0.01"
                      min="0"
                      max="2"
                    />
                  </div>
                  <div className="form-group third">
                    <label htmlFor="kcEnd">{t("End (Kc end)")}</label>
                    <input
                      type="number"
                      id="kcEnd"
                      name="kcEnd"
                      value={cropData.kcEnd}
                      onChange={handleInputChange}
                      className="form-control"
                      step="0.01"
                      min="0"
                      max="2"
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button className="save-button" onClick={saveCropData} disabled={saving}>
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> {t("Saving...")}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i> {t("Save Crop Data")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="kc-calculator-results">
          <div className="card">
            <div className="card-header">
              <h2>{t("Crop Coefficient (Kc) Curve")}</h2>
            </div>
            <div className="card-body">
              {kcValues.length > 0 ? (
                <div className="kc-chart-container">
                  <div className="kc-chart">
                    <svg viewBox="0 0 800 400" className="kc-chart-svg">
                      {/* X and Y axes */}
                      <line x1="50" y1="350" x2="750" y2="350" stroke="#333" strokeWidth="2" />
                      <line x1="50" y1="50" x2="50" y2="350" stroke="#333" strokeWidth="2" />

                      {/* X-axis labels */}
                      <text x="400" y="390" textAnchor="middle" className="axis-label">
                        {t("Days After Planting")}
                      </text>

                      {/* Y-axis labels */}
                      <text x="20" y="200" textAnchor="middle" transform="rotate(-90, 20, 200)" className="axis-label">
                        {t("Crop Coefficient (Kc)")}
                      </text>

                      {/* Y-axis ticks */}
                      {[0, 0.5, 1.0, 1.5, 2.0].map((tick, index) => (
                        <g key={index}>
                          <line
                            x1="45"
                            y1={350 - tick * 150}
                            x2="55"
                            y2={350 - tick * 150}
                            stroke="#333"
                            strokeWidth="1"
                          />
                          <text x="40" y={350 - tick * 150 + 5} textAnchor="end" className="tick-label">
                            {tick.toFixed(1)}
                          </text>
                        </g>
                      ))}

                      {/* X-axis ticks */}
                      {kcValues.length > 0 &&
                        [0, 0.25, 0.5, 0.75, 1].map((percent, index) => {
                          const day = Math.floor(kcValues.length * percent)
                          const x = 50 + 700 * percent
                          return (
                            <g key={index}>
                              <line x1={x} y1="345" x2={x} y2="355" stroke="#333" strokeWidth="1" />
                              <text x={x} y="370" textAnchor="middle" className="tick-label">
                                {day}
                              </text>
                            </g>
                          )
                        })}

                      {/* Growth stage backgrounds */}
                      {kcValues.length > 0 && (
                        <>
                          <rect
                            x="50"
                            y="50"
                            width={700 * (Number.parseInt(cropData.initialStage) / kcValues.length)}
                            height="300"
                            fill="#e8f5e9"
                            opacity="0.5"
                          />
                          <rect
                            x={50 + 700 * (Number.parseInt(cropData.initialStage) / kcValues.length)}
                            y="50"
                            width={700 * (Number.parseInt(cropData.developmentStage) / kcValues.length)}
                            height="300"
                            fill="#c8e6c9"
                            opacity="0.5"
                          />
                          <rect
                            x={
                              50 +
                              700 *
                                ((Number.parseInt(cropData.initialStage) + Number.parseInt(cropData.developmentStage)) /
                                  kcValues.length)
                            }
                            y="50"
                            width={700 * (Number.parseInt(cropData.midSeasonStage) / kcValues.length)}
                            height="300"
                            fill="#a5d6a7"
                            opacity="0.5"
                          />
                          <rect
                            x={
                              50 +
                              700 *
                                ((Number.parseInt(cropData.initialStage) +
                                  Number.parseInt(cropData.developmentStage) +
                                  Number.parseInt(cropData.midSeasonStage)) /
                                  kcValues.length)
                            }
                            y="50"
                            width={700 * (Number.parseInt(cropData.lateSeasonStage) / kcValues.length)}
                            height="300"
                            fill="#81c784"
                            opacity="0.5"
                          />
                        </>
                      )}

                      {/* KC curve */}
                      {kcValues.length > 0 && (
                        <polyline
                          points={kcValues
                            .map((point, index) => {
                              const x = 50 + 700 * (index / (kcValues.length - 1))
                              const y = 350 - point.kc * 150
                              return `${x},${y}`
                            })
                            .join(" ")}
                          fill="none"
                          stroke="#4caf50"
                          strokeWidth="3"
                        />
                      )}

                      {/* Stage labels */}
                      {kcValues.length > 0 && (
                        <>
                          <text
                            x={50 + 700 * (Number.parseInt(cropData.initialStage) / kcValues.length / 2)}
                            y="70"
                            textAnchor="middle"
                            className="stage-label"
                          >
                            {t("Initial")}
                          </text>
                          <text
                            x={
                              50 +
                              700 * (Number.parseInt(cropData.initialStage) / kcValues.length) +
                              700 * (Number.parseInt(cropData.developmentStage) / kcValues.length / 2)
                            }
                            y="70"
                            textAnchor="middle"
                            className="stage-label"
                          >
                            {t("Development")}
                          </text>
                          <text
                            x={
                              50 +
                              700 *
                                ((Number.parseInt(cropData.initialStage) + Number.parseInt(cropData.developmentStage)) /
                                  kcValues.length) +
                              700 * (Number.parseInt(cropData.midSeasonStage) / kcValues.length / 2)
                            }
                            y="70"
                            textAnchor="middle"
                            className="stage-label"
                          >
                            {t("Mid-Season")}
                          </text>
                          <text
                            x={
                              50 +
                              700 *
                                ((Number.parseInt(cropData.initialStage) +
                                  Number.parseInt(cropData.developmentStage) +
                                  Number.parseInt(cropData.midSeasonStage)) /
                                  kcValues.length) +
                              700 * (Number.parseInt(cropData.lateSeasonStage) / kcValues.length / 2)
                            }
                            y="70"
                            textAnchor="middle"
                            className="stage-label"
                          >
                            {t("Late Season")}
                          </text>
                        </>
                      )}
                    </svg>
                  </div>

                  <div className="kc-chart-legend">
                    <div className="legend-item">
                      <div className="legend-color" style={{ backgroundColor: "#e8f5e9" }}></div>
                      <span>{t("Initial Stage")}</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color" style={{ backgroundColor: "#c8e6c9" }}></div>
                      <span>{t("Development Stage")}</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color" style={{ backgroundColor: "#a5d6a7" }}></div>
                      <span>{t("Mid-Season Stage")}</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color" style={{ backgroundColor: "#81c784" }}></div>
                      <span>{t("Late Season Stage")}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-chart">
                  <i className="fas fa-chart-line"></i>
                  <p>{t("Enter crop data to generate Kc curve")}</p>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2>{t("Kc Values Table")}</h2>
            </div>
            <div className="card-body">
              {kcValues.length > 0 ? (
                <div className="kc-table-container">
                  <table className="kc-table">
                    <thead>
                      <tr>
                        <th>{t("Day")}</th>
                        <th>{t("Stage")}</th>
                        <th>{t("Kc Value")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kcValues
                        .filter((_, index) => index % 5 === 0)
                        .map((value) => (
                          <tr key={value.day}>
                            <td>{value.day}</td>
                            <td>{t(value.stage.charAt(0).toUpperCase() + value.stage.slice(1))}</td>
                            <td>{value.kc.toFixed(2)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-table">
                  <i className="fas fa-table"></i>
                  <p>{t("Enter crop data to generate Kc values")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProjectKcCalculator
